const getSyncFieldName = (syncName, fieldName) => {
  return `sync-${syncName}-${fieldName}`;
};

const coerceBool = v => {
  if (v === true || v === 'true' || v === '1' || v === 1 || v === 'on') return true;
  if (v === false || v === 'false' || v === '0' || v === 0 || v === 'off') return false;
  return false;
};

/**
 * If displayIf is provided, show when the controlling field resolves truthy.
 * Falls back to field defaultValue when formState is unset.
 * Recursively checks parent field visibility to handle chained conditions.
 *
 * A scoped reference ('parentGTag.gtgEnabled') resolves the controlling field
 * against another sync scope's form state. Scoped references are live-value
 * only: the other scope's field metadata isn't available here, so an unset
 * controlling field resolves falsy (hidden).
 */
const shouldDisplayField = (displayIf, formState, syncName, defaults, expectedValue) => {
  if (!displayIf) return true;

  const scopeSeparatorIndex = displayIf.indexOf('.');
  const scope = scopeSeparatorIndex === -1 ? null : displayIf.slice(0, scopeSeparatorIndex);
  const controllingField = scope === null ? displayIf : displayIf.slice(scopeSeparatorIndex + 1);

  const key = getSyncFieldName(scope ?? syncName, controllingField);
  const live = formState[key]?.value;

  if (scope !== null) {
    return coerceBool(live);
  }

  const displayIfField = defaults.find(field => field.name === displayIf);

  // Recursively check if the parent field itself is visible
  if (displayIfField?.displayIf) {
    const parentVisible = shouldDisplayField(
      displayIfField.displayIf,
      formState,
      syncName,
      defaults,
      displayIfField.displayIfValue
    );
    if (!parentVisible) {
      return false; // Parent is hidden, so this field should also be hidden
    }
  }

  // fallback to the field's default if not set yet
  const resolved = typeof live !== 'undefined' ? live : displayIfField?.defaultValue;

  // Handle Select fields with string values via expectedValue (avoid hardcoding)
  if (displayIfField?.type === 'SELECT' && typeof resolved === 'string') {
    if (typeof expectedValue === 'string') {
      return resolved === expectedValue;
    }
    // Default: non-empty strings are truthy
    return resolved !== '';
  }

  return coerceBool(resolved);
};

export { getSyncFieldName, shouldDisplayField, coerceBool };
